import { useState, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import lang from "../../Translation/Ladning.json";

export default function PrestasiChart({ data = [] }) {
  const text = lang.Chart;
  const [selectedDate, setSelectedDate] = useState("2025-06-29");

  // 🔹 Prepare chart data dynamically
  const chartData = useMemo(() => {
    // Calculate totals by zone
    const zoneTotals = {};

    data.forEach((item) => {
      const zoneKey = item.zone?.toLowerCase() || "unknown";
      if (!zoneTotals[zoneKey]) {
        zoneTotals[zoneKey] = {
          charge: 0,
          arrears: 0,
          paymentAmount: 0,
        };
      }
      zoneTotals[zoneKey].charge += item.charge || 0;
      zoneTotals[zoneKey].arrears += item.arrears || 0;
      zoneTotals[zoneKey].paymentAmount += item.paymentAmount || 0;
    });

    // Prepare 4 zones (zone1–zone4) with fallback 0 values
    const zones = ["zone1", "zone2", "zone3", "zone4"];
    const rows = [
      { name: text.overall_charge },
      { name: text.arrears },
      { name: text.total_payment },
      { name: text.net_payment },
    ];

    rows.forEach((row) => {
      zones.forEach((z) => {
        const zone = zoneTotals[z] || { charge: 0, arrears: 0, paymentAmount: 0 };
        if (row.name === text.overall_charge) row[z] = zone.charge;
        else if (row.name === text.arrears) row[z] = zone.arrears;
        else if (row.name === text.total_payment) row[z] = zone.paymentAmount;
        else if (row.name === text.net_payment)
          row[z] = zone.paymentAmount - zone.arrears;
      });
    });

    return rows;
  }, [data, text]);

  // 🔹 Visibility control for zones
  const [visibleZones, setVisibleZones] = useState({
    zone1: true,
    zone2: true,
    zone3: true,
    zone4: true,
  });

  const handleLegendClick = (zoneKey) => {
    setVisibleZones((prev) => ({
      ...prev,
      [zoneKey]: !prev[zoneKey],
    }));
  };

  const zoneColors = {
    zone1: "#f59e0b", // amber
    zone2: "#6366f1", // indigo
    zone3: "#06b6d4", // cyan
    zone4: "#22c55e", // green
  };

  return (
    <div className="bg-white shadow-md rounded-md p-6 w-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between border-b pb-3 mb-4">
        <h2 className="text-lg font-semibold text-gray-800">
          {text.performance_summary} ({text.zone_1}, {text.zone_2}, {text.zone_3},{" "}
          {text.zone_4}) —{" "}
          <span className="text-indigo-600 font-medium">
            {new Date(selectedDate).toLocaleDateString("en-GB")}
          </span>
        </h2>

        <div className="flex items-center gap-2 mt-3 md:mt-0">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>
      </div>

      {/* Chart */}
      <div className="w-full h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 10, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis
              tickFormatter={(value) => `RM ${(value / 1_000_000).toFixed(1)}M`}
              tick={{ fontSize: 12 }}
            />
            <Tooltip
              formatter={(value) =>
                `RM ${value.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}`
              }
            />

            {/* Dynamically render visible zones */}
            {Object.entries(visibleZones).map(
              ([zoneKey, visible]) =>
                visible && (
                  <Bar
                    key={zoneKey}
                    dataKey={zoneKey}
                    fill={zoneColors[zoneKey]}
                    name={text[zoneKey] || zoneKey.toUpperCase()}
                    radius={[6, 6, 0, 0]}
                  />
                )
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Custom Legend */}
      <div className="flex justify-center flex-wrap gap-3 mt-3">
        {Object.keys(visibleZones).map((zoneKey) => (
          <button
            key={zoneKey}
            onClick={() => handleLegendClick(zoneKey)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition ${
              visibleZones[zoneKey] ? "" : "line-through opacity-60"
            }`}
          >
            <div
              className={`w-3 h-3 rounded-sm ${
                visibleZones[zoneKey]
                  ? `bg-[${zoneColors[zoneKey]}]`
                  : "bg-gray-300"
              }`}
              style={{
                backgroundColor: visibleZones[zoneKey]
                  ? zoneColors[zoneKey]
                  : "#d1d5db",
              }}
            ></div>
            {text[zoneKey] || zoneKey.toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  );
}
